import {
  DATE_VALIDATION_TYPE,
  DIGIT_REGEX,
  VALIDATION_MESSAGES,
  Constants
} from '@/utils/constants/commonConstants';
import {
  createValidationFunction,
  isInProgress,
  isPublished
} from '@/utils/commons';

const VALIDATION_FUNCTIONS = {
  validateSixDigits: createValidationFunction(
    DIGIT_REGEX.SIX_DIGITS_DECIMAL_TWO_PLACES,
    VALIDATION_MESSAGES.POINTS_EARNED_6_DIGITS
  ),
  validateSixDigitsAlt: createValidationFunction(
    DIGIT_REGEX.SIX_DIGITS,
    VALIDATION_MESSAGES.POINTS_EARNED_6_DIGITS_ALT
  ),
  validateTwoDigits: createValidationFunction(
    DIGIT_REGEX.TWO_DIGITS,
    VALIDATION_MESSAGES.DAILY_LOGIN_LIMIT_2_DIGITS
  ),
  validateFourDigits: createValidationFunction(
    DIGIT_REGEX.FOUR_DIGITS,
    VALIDATION_MESSAGES.ALL_TIME_LOGIN_LIMIT_4_DIGITS
  ),
  validateLocalCurrencyTwoDecimals: createValidationFunction(
    DIGIT_REGEX.LOCAL_CURRENCY_TWO_DECIMALS,
    VALIDATION_MESSAGES.LOCAL_CURRENCY_TWO_DECIMALS
  )
};

// Helper functions to reduce duplication
const createPointsEarnedControl = (
  scope,
  className = 'box-size-width text-sm',
  required = true,
  hideCrossIcon = false,
  label = 'Points Earned *',
  placeholder = 'Enter points'
) => ({
  type: 'Control',
  scope,
  component: 'textfield',
  placeholder,
  label,
  className: `${className}${hideCrossIcon ? ' hide-cross-icon' : ''}`,
  isDark: true,
  required,
  validationRules: [
    {type: 'required', msg: ` is required`},
    {type: 'number', msg: ` must be a number`},
    {
      type: 'custom',
      validate: VALIDATION_FUNCTIONS.validateSixDigits
    }
  ],
  hideFeedbackIcon: true
});

const createDateControl = (
  scope,
  label,
  placeholder,
  disabled = false,
  disabledHelperText = '',
  dependentDate = null,
  validationRules = []
) => ({
  type: 'Control',
  scope,
  component: 'datepicker',
  className: 'box-size-width',
  placeholder,
  label,
  isDark: true,
  required: false,
  disabled,
  disabledHelperText,
  dependentDate,
  validationRules,
  hideFeedbackIcon: true
});

const createTextfieldControl = (
  scope,
  label,
  placeholder,
  className = 'box-size-width text-sm',
  required = false,
  validationRules = [],
  disabled = false,
  helpText = ''
) => ({
  type: 'Control',
  scope,
  component: 'textfield',
  placeholder,
  label,
  className,
  isDark: true,
  required,
  validationRules,
  disabled,
  helpText,
  hideFeedbackIcon: true
});

const createActivityNameControl = (label, placeholder, formData) =>
  createTextfieldControl(
    '#/properties/activityName',
    label,
    placeholder,
    'box-size-width text-sm',
    true,
    [{type: 'required', msg: ` is required`}],
    formData?.status != undefined
  );

const createDropdownControl = (
  scope,
  label,
  placeholder,
  className = 'box-size-width text-sm',
  required = false,
  validationRules = [],
  disabled = false
) => ({
  type: 'Control',
  scope,
  component: 'dropdownControl',
  placeholder,
  label,
  className,
  isDark: true,
  required,
  validationRules,
  disabled,
  hideFeedbackIcon: true
});

const createToggleControl = (scope, label, className) => ({
  type: 'Control',
  scope,
  component: 'toggle',
  label,
  className
});

// UI Schema for Activity Selection
export const activitySelectionUiSchema = (formData) => {
  const disable = shouldDisableActivityTypeSelection(formData);
  return {
    type: 'HorizontalLayout',
    className: 'w-auto',
    elements: [
      {
        type: 'HorizontalLayout',
        className: 'avail-first-row',
        elements: [
          createDropdownControl(
            '#/properties/activityType',
            'Activity Type *',
            'Choose Activity Type',
            'box-size-width text-sm dropdown-max-width',
            true,
            [{type: 'required', msg: ` is required`}],
            disable
          )
        ]
      }
    ]
  };
};

// UI Schema for Login Activity
export const loginActivityJsonSchema = () => ({
  type: 'object',
  properties: {
    pointsEarned: {
      errorMessage: 'This field is required',
      type: 'string',
      restrictDecimal: false
    },
    dailyLoginLimit: {
      type: 'string',
      restrictDecimal: true
    },
    allTimeLoginLimit: {
      type: 'string',
      restrictDecimal: true
    }
  },
  required: []
});

export const loginActivityUiSchema = () => ({
  type: 'VerticalLayout',
  className: 'w-full',
  elements: [
    {
      type: 'HorizontalLayout',
      className: '',
      elements: [
        createPointsEarnedControl('#/properties/pointsEarned'),
        createTextfieldControl(
          '#/properties/dailyLoginLimit',
          'Daily Login Limit*',
          'Enter daily limit',
          'box-size-width text-sm',
          false,
          [
            {type: 'number', msg: ` must be a number`},
            {
              type: 'custom',
              validate: VALIDATION_FUNCTIONS.validateTwoDigits
            }
          ]
        ),
        createTextfieldControl(
          '#/properties/allTimeLoginLimit',
          'All Time Login Limit',
          'Enter total limit',
          'box-size-width text-sm',
          false,
          [
            {type: 'number', msg: ` must be a number`},
            {
              type: 'custom',
              validate: VALIDATION_FUNCTIONS.validateFourDigits
            }
          ],
          false,
          'Max number of logins to receive points'
        )
      ]
    }
  ]
});

// Schema for Welcome Activity
export const welcomeActivityJsonSchema = {
  type: 'number',
  properties: {
    pointsEarned: {
      type: 'string',
      restrictDecimal: false
    }
  },
  required: []
};

// UI Schema for Welcome Activity
export const welcomeActivityUiSchema = {
  type: 'HorizontalLayout',
  className: 'partner-filters',
  elements: [
    {
      type: 'HorizontalLayout',
      className: 'avail-first-row',
      elements: [createPointsEarnedControl('#/properties/pointsEarned')]
    }
  ]
};

// Schema for Welcome Activity Availability
export const welcomeActivityAvailabilityJsonSchema = {
  type: 'object',
  properties: {
    startDate: {
      type: 'string'
    },
    hasEndDate: {
      type: 'boolean',
      default: false
    }
  },
  required: []
};

export const welcomeActivityHasEndDateSchema = {
  type: 'object',
  properties: {
    hasEndDate: {
      type: 'boolean',
      default: false
    }
  },
  required: []
};

export const welcomeActivityEndDateJsonSchema = {
  type: 'object',
  properties: {
    endDate: {
      type: 'string'
    }
  },
  required: []
};
const isEditingActivity = (formData) =>
  !!(formData?.id || formData?.uuid || formData?.activityId);

const isDraftStatus = (status) =>
  (status ?? '').trim().toLowerCase() === Constants.Draft.toLowerCase();

const shouldDisableActivityTypeSelection = (formData) => {
  if (formData?.activityType === 'Points Expiration') {
    return isEditingActivity(formData);
  }

  return (
    shouldDisable(formData) ||
    (isDraftStatus(formData?.status) && isEditingActivity(formData))
  );
};

const shouldDisable = (formData) => {
  if (formData?.activityType === 'Points Expiration') {
    return !!(formData?.id || formData?.activityId);
  }

  const isPublishedStatus = isPublished(formData?.status);
  const isInProgressStatus = isInProgress(formData?.status);
  return isPublishedStatus || isInProgressStatus;
};

/** Survey/Video: start date editable for Draft and Published; locked when In Progress. */
const shouldDisableSurveyVideoStartDate = (formData) =>
  isInProgress(formData?.status);

/** Game: start date editable for Draft and Published; locked when In Progress. */
const shouldDisableGameStartDate = (formData) => isInProgress(formData?.status);

// UI Schema for Welcome Activity Availability
export const welcomeActivityAvailabilityUiSchema = (
  endDate,
  startDate,
  formData
) => {
  const disable = shouldDisable(formData);
  return {
    type: 'HorizontalLayout',
    className: 'partner-filters',
    elements: [
      {
        type: 'HorizontalLayout',
        className: 'items-center',
        elements: [
          createDateControl(
            '#/properties/startDate',
            'Active Start Date *',
            'Choose date',
            disable,
            disable
              ? ''
              : 'Please select end date before selecting start date.',
            endDate || startDate,
            [
              {type: 'required', msg: ` is required`},
              {
                type: DATE_VALIDATION_TYPE.START_DATE,
                msg: ` cannot be in the past.`,
                endDate: endDate
              },
              {
                type: DATE_VALIDATION_TYPE.START_DATE_TODAY,
                msg: `  Target can only start from a date in future`
              }
            ]
          )
        ]
      }
    ]
  };
};

export const welcomeActivityHasEndUiSchema = {
  type: 'HorizontalLayout',
  className: 'partner-filters',
  elements: [
    {
      type: 'HorizontalLayout',
      className: 'items-center',
      elements: [
        createToggleControl(
          '#/properties/hasEndDate',
          'Has End Date',
          'mt-8 justify-between  w-44'
        )
      ]
    }
  ]
};

export const welcomeActivityEndDateUiSchema = (startDate, endDate) => ({
  type: 'HorizontalLayout',
  className: 'partner-filters',
  elements: [
    {
      type: 'VerticalLayout',
      className: '',
      elements: [
        createDateControl(
          '#/properties/endDate',
          'Active End Date *',
          'Choose date',
          !startDate,
          'Please select start date before selecting end date.',
          endDate || startDate,
          [
            {type: 'required', msg: ` is required`},
            {
              type: DATE_VALIDATION_TYPE.END_DATE,
              msg: ` can't be less than Start date.`,
              startDate: startDate
            }
          ]
        )
      ]
    }
  ]
});

// Schema for Points On Order Delivery Activity
export const pointsOnOrderDeliveryActivityJsonSchema = {
  type: 'object',
  properties: {
    activityName: {
      type: 'string',
      helperText: '',
      errorMessage: 'This field is required'
    },
    orderType: {
      type: 'string',
      helperText: '',
      errorMessage: 'This field is required',
      options: [
        {
          id: 1,
          displayText: 'Online'
        },
        {
          id: 2,
          displayText: 'Offline'
        },
        {
          id: 3,
          displayText: 'Both'
        }
      ]
    }
  },
  required: []
};

// UI Schema for Points On Order Delivery Activity
export const pointsOnOrderDeliveryActivityUiSchema = (formData) => ({
  type: 'HorizontalLayout',
  className: 'w-full',
  elements: [
    {
      type: 'HorizontalLayout',
      className: 'w-auto',
      elements: [
        createActivityNameControl(
          'Activity Name *',
          'Enter activity name',
          formData
        ),
        createDropdownControl(
          '#/properties/orderType',
          'Order Type *',
          'Select order type',
          'box-size-width text-sm'
        )
      ]
    }
  ]
});

// Schema for Availability
export const availabilityJsonSchema = {
  type: 'object',
  properties: {
    startDate: {
      type: 'string'
    },
    endDate: {
      type: 'string'
    },
    receivePoints: {
      type: 'string',
      helperText: '',
      errorMessage: 'This field is required',
      options: [
        {value: 'Instantly', displayText: 'Instantly'},
        {value: 'Daily', displayText: 'Daily'},
        {value: 'Weekly', displayText: 'Weekly'},
        {value: 'Biweekly', displayText: 'Biweekly'},
        {value: 'Monthly', displayText: 'Monthly'}
      ]
    }
  },
  required: []
};

// UI Schema for Availability
export const availabilityUiSchema = (startDate, endDate, formData) => ({
  type: 'VerticalLayout',
  className: 'partner-filters',
  elements: [
    {
      type: 'HorizontalLayout',
      className: 'w-full activity-section_availability-block',
      elements: availabilityDateControls(startDate, endDate, formData)
    }
  ]
});

// Shared date controls for availability UI schemas
const availabilityDateControls = (startDate, endDate, formData) => {
  const disable = shouldDisable(formData);
  return [
    createDateControl(
      '#/properties/startDate',
      'Active Start Date *',
      'Choose date',
      disable,
      disable ? '' : 'Please select end date before selecting start date.',
      endDate,
      [
        {type: 'required', msg: ` is required`},
        {
          type: DATE_VALIDATION_TYPE.START_DATE,
          msg: ` can't be in the past.`,
          endDate: endDate
        },
        {
          type: DATE_VALIDATION_TYPE.START_DATE_TODAY,
          msg: `  Target can only start from a date in future`
        }
      ]
    ),
    createDateControl(
      '#/properties/endDate',
      'Active End Date *',
      'Choose date',
      !startDate,
      'Please select start date before selecting end date.',
      startDate,
      [
        {type: 'required', msg: ` is required`},
        {
          type: DATE_VALIDATION_TYPE.END_DATE,
          msg: ` can't be less than Start date.`,
          startDate: startDate
        }
      ]
    )
  ];
};

export const allAvailabilityUiSchema = (startDate, endDate, formData) => ({
  type: 'HorizontalLayout',
  className: 'w-full',
  elements: [
    {
      type: 'HorizontalLayout',
      className: 'w-full activity-section_availability-block',
      elements: [
        ...availabilityDateControls(startDate, endDate, formData),
        createDropdownControl(
          '#/properties/receivePoints',
          'Frequency *',
          'Choose Frequency',
          'box-size-width mt-1',
          true,
          [{type: 'required', msg: ` is required`}]
        )
      ]
    }
  ]
});

// Schema for Points allocation
export const pointsAllocationJsonSchema = {
  type: 'object',
  properties: {
    localCurrency: {
      type: 'string',
      helperText: '',
      errorMessage: 'This field is required'
    },
    pointsEarned: {
      type: 'string',
      helperText: '',
      errorMessage: 'This field is required'
    }
  },
  required: []
};
export const pointsAllocationJsonSchemaForEG = {
  type: 'object',
  properties: {
    numberOfCases: {
      type: 'string',
      helperText: '',
      errorMessage: 'This field is required',
      restrictDecimal: true
    },
    pointsEarned: {
      type: 'string',
      helperText: '',
      errorMessage: 'This field is required',
      restrictDecimal: false
    }
  },
  required: []
};

// UI Schema for Points Allocation
export const pointsAllocationUiSchema = {
  type: 'VerticalLayout',
  className: 'partner-filters flex w-auto',
  elements: [
    {
      type: 'HorizontalLayout',
      className: 'flex gap-2',
      elements: [
        createTextfieldControl(
          '#/properties/localCurrency',
          'Local Currency *',
          'Enter amount',
          'box-size-width text-sm',
          true,
          [
            {type: 'required', msg: ` is required`},
            {type: 'decimal', msg: 'must be a number'}
          ]
        ),
        createPointsEarnedControl(
          '#/properties/pointsEarned',
          'box-size-width hide-cross-icon text-sm',
          true,
          true
        )
      ]
    }
  ]
};
export const pointsAllocationUiSchemaForEG = {
  type: 'VerticalLayout',
  className: 'partner-filters w-full sm:w-2/4 flex flex-col',
  elements: [
    {
      type: 'HorizontalLayout',
      className: 'w-4/4 flex gap-4',
      elements: [
        createTextfieldControl(
          '#/properties/numberOfCases',
          'Number Of Cases*',
          'Enter amount',
          'text-sm w-full',
          true,
          [
            {type: 'required', msg: ` is required`},
            {type: 'decimal', msg: 'must be a number'}
          ]
        ),
        createPointsEarnedControl(
          '#/properties/pointsEarned',
          'hide-cross-icon text-sm w-full',
          true,
          true
        )
      ]
    }
  ]
};

// Schema for Point Expiration
export const pointExpirationJsonSchema = {
  type: 'object',
  properties: {
    noOfDays: {
      type: 'integer',
      pattern: '^\\d+$',
      helperText: '',
      errorMessage: 'This field is required',
      restrictDecimal: true
    }
  },
  required: ['noOfDays']
};

// UI Schema for Point Expiration
export const pointExpirationUiSchema = {
  type: 'VerticalLayout',
  className: 'partner-filters',
  elements: [
    {
      type: 'HorizontalLayout',
      className: 'avail-first-row',
      elements: [
        createTextfieldControl(
          '#/properties/noOfDays',
          'Frequency *',
          'Enter days (1-365)',
          'box-size-width text-sm',
          true,
          [
            {type: 'required', msg: ` is required`},
            {type: 'number', msg: ` must be a number`},
            {
              type: 'custom',
              validate: (value) => {
                // Check if value contains only digits
                if (!/^\d+$/.test(value)) {
                  return 'Only numbers are allowed';
                }
                const num = parseInt(value, 10);
                if (isNaN(num) || num < 1 || num > 365) {
                  return 'Value must be between 1 and 365';
                }
                return undefined;
              }
            }
          ]
        )
      ]
    }
  ]
};

// Schema for Pay by Points on Order Delivery (activityName is auto-generated, not shown in UI)
export const payByPointsJsonSchema = {
  type: 'object',
  properties: {
    pointsToRedeem: {
      type: 'string',
      helperText: '',
      errorMessage: 'This field is required',
      restrictDecimal: true
    },
    localCurrencyValue: {
      type: 'string',
      helperText: '',
      errorMessage: 'This field is required'
    }
  },
  required: []
};

// UI Schema for Pay by Points on Order Delivery
export const payByPointsUiSchema = (formData, localCurrencyCode) => {
  const currencyLabel = localCurrencyCode
    ? `Local Currency Value (${localCurrencyCode}) *`
    : 'Local Currency Value *';
  return {
    type: 'VerticalLayout',
    className: 'w-full',
    elements: [
      {
        type: 'HorizontalLayout',
        className: 'flex w-auto flex-wrap gap-4',
        elements: [
          createTextfieldControl(
            '#/properties/pointsToRedeem',
            'Points to Redeem *',
            'Enter points',
            'box-size-width text-sm',
            true,
            [
              {type: 'required', msg: ` is required`},
              {type: 'number', msg: ` must be a number`},
              {
                type: 'custom',
                validate: VALIDATION_FUNCTIONS.validateSixDigitsAlt
              }
            ]
          ),
          createTextfieldControl(
            '#/properties/localCurrencyValue',
            currencyLabel,
            'Enter amount (e.g. 10.0, 10.50)',
            'box-size-width text-sm',
            true,
            [
              {type: 'required', msg: ` is required`},
              {type: 'number', msg: ` must be a number`},
              {
                type: 'custom',
                validate: VALIDATION_FUNCTIONS.validateLocalCurrencyTwoDecimals
              }
            ]
          )
        ]
      }
    ]
  };
};

// Survey activity — name + points (availability dates are a separate JsonForms block)
export const surveyActivityJsonSchema = {
  type: 'object',
  properties: {
    activityName: {
      type: 'string',
      helperText: '',
      errorMessage: 'This field is required'
    },
    pointsEarned: {
      type: 'string',
      helperText: '',
      errorMessage: 'This field is required'
    },
    allTimeLimit: {
      type: 'string',
      helperText: '',
      errorMessage: 'This field is required'
    }
  },
  required: []
};

export const surveyActivityUiSchema = (formData) => ({
  type: 'HorizontalLayout',
  className:
    'survey-activity-name-points flex flex-wrap items-end gap-4 lg:flex-nowrap',
  elements: [
    createActivityNameControl('Survey Name *', 'Survey Name', formData),
    createPointsEarnedControl(
      '#/properties/pointsEarned',
      'box-size-width text-sm',
      true,
      false,
      'Points Earned *',
      'Points Earned'
    ),
    createTextfieldControl(
      '#/properties/allTimeLimit',
      'All Time Limit *',
      'Enter limit',
      'box-size-width text-sm',
      true,
      [
        {type: 'required', msg: ` is required`},
        {type: 'number', msg: ` must be a number`},
        {
          type: 'custom',
          validate: validatePositiveWholeNumber
        }
      ]
    )
  ]
});

export const surveyAvailabilityJsonSchema = {
  type: 'object',
  properties: {
    startDate: {type: 'string'},
    endDate: {type: 'string'}
  },
  required: []
};

const surveyAvailabilityDateControls = (startDate, endDate, formData) => {
  const disableStartDate = shouldDisableSurveyVideoStartDate(formData);
  return [
    createDateControl(
      '#/properties/startDate',
      'Active Start Date *',
      'Choose date',
      disableStartDate,
      disableStartDate
        ? ''
        : 'Please select end date before selecting start date.',
      endDate,
      [
        {type: 'required', msg: ` is required`},
        {
          type: DATE_VALIDATION_TYPE.START_DATE,
          msg: ` can't be in the past.`,
          endDate: endDate
        },
        {
          type: DATE_VALIDATION_TYPE.START_DATE_TODAY,
          msg: `  Target can only start from a date in future`
        }
      ]
    ),
    createDateControl(
      '#/properties/endDate',
      'Active End Date *',
      'Choose date',
      !startDate,
      'Please select start date before selecting end date.',
      startDate,
      [
        {type: 'required', msg: ` is required`},
        {
          type: DATE_VALIDATION_TYPE.END_DATE,
          msg: 'End Date cannot be earlier than Start Date.',
          startDate: startDate,
          excludeLabel: true
        }
      ]
    )
  ];
};

export const surveyAvailabilityUiSchema = (startDate, endDate, formData) => ({
  type: 'VerticalLayout',
  className: 'partner-filters',
  elements: [
    {
      type: 'HorizontalLayout',
      className: 'w-full activity-section_availability-block',
      elements: surveyAvailabilityDateControls(startDate, endDate, formData)
    }
  ]
});

const validatePositiveWholeNumber = (value) => {
  if (value === '' || value === undefined || value === null) {
    return undefined;
  }
  const n = Number(String(value).trim());
  if (!Number.isFinite(n) || n <= 0 || !Number.isInteger(n)) {
    return ' must be a positive whole number';
  }
  return undefined;
};

const validateNonNegativeNumber = (value) => {
  if (value === '' || value === undefined || value === null) {
    return undefined;
  }
  const trimmed = String(value).trim();
  if (!/^\d+$/.test(trimmed)) {
    return ' must be a number';
  }
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) {
    return ' must be a non-negative number';
  }
  return undefined;
};

const validateVideoAllTimeLimitPositive = validatePositiveWholeNumber;

/** Wheel of Fortune activity — name, budget, budget period, daily play limit */
export const wheelOfFortuneActivityJsonSchema = {
  type: 'object',
  properties: {
    activityName: {
      type: 'string',
      helperText: '',
      errorMessage: 'This field is required'
    },
    pointsEarned: {
      type: 'number',
      helperText: '',
      errorMessage: 'This field is required'
    },
    budgetPeriod: {
      type: 'string',
      helperText: '',
      errorMessage: 'This field is required',
      options: [
        {id: 1, displayText: 'Daily', value: 'Daily'},
        {id: 2, displayText: 'Weekly', value: 'Weekly'},
        {id: 3, displayText: 'Monthly', value: 'Monthly'},
        {id: 4, displayText: 'Yearly', value: 'Yearly'}
      ]
    }
  },
  required: []
};

export const wheelOfFortuneActivityUiSchema = (formData) => ({
  type: 'VerticalLayout',
  className: 'wheel-of-fortune-activity-setup flex flex-col gap-4',
  elements: [
    {
      type: 'HorizontalLayout',
      className:
        'wheel-of-fortune-name-budget flex flex-wrap items-end gap-4 lg:flex-nowrap',
      elements: [
        createActivityNameControl(
          'Activity Name *',
          'Enter activity name',
          formData
        ),
        {
          ...createTextfieldControl(
            '#/properties/pointsEarned',
            'Budget *',
            'Enter budget',
            'box-size-width text-sm',
            true,
            [{type: 'required', msg: ' is required'}]
          ),
          maxLength: 10
        },
        createDropdownControl(
          '#/properties/budgetPeriod',
          'Budget Period *',
          'Select budget period',
          'box-size-width text-sm',
          true,
          [{type: 'required', msg: ` is required`}]
        )
      ]
    }
  ]
});

/** Video activity — name, points, all-time limit (availability dates use same block as Survey) */
export const videoActivityJsonSchema = {
  type: 'object',
  properties: {
    activityName: {
      type: 'string',
      helperText: '',
      errorMessage: 'This field is required'
    },
    pointsEarned: {
      type: 'string',
      helperText: '',
      errorMessage: 'This field is required'
    },
    allTimeLimit: {
      type: 'string',
      helperText: '',
      errorMessage: 'This field is required'
    }
  },
  required: []
};

export const videoActivityUiSchema = (formData) => ({
  type: 'HorizontalLayout',
  className:
    'video-activity-name-points flex flex-wrap items-end gap-4 lg:flex-nowrap',
  elements: [
    createActivityNameControl('Video Name *', 'Video Name', formData),
    createPointsEarnedControl('#/properties/pointsEarned'),
    createTextfieldControl(
      '#/properties/allTimeLimit',
      'All Time Limit *',
      'Enter limit',
      'box-size-width text-sm',
      true,
      [
        {type: 'required', msg: ` is required`},
        {type: 'number', msg: ` must be a number`},
        {
          type: 'custom',
          validate: validateVideoAllTimeLimitPositive
        }
      ]
    )
  ]
});

// Schema for Game Activity Setup
export const gameActivitySetupJsonSchema = {
  type: 'object',
  properties: {
    activityName: {
      type: 'string',
      helperText: '',
      errorMessage: 'This field is required'
    }
  },
  required: []
};

// UI Schema for Game Activity Setup (Activity Name field)
export const gameActivitySetupUiSchema = (formData) => ({
  type: 'HorizontalLayout',
  className: 'flex flex-wrap items-end gap-4 lg:flex-nowrap',
  elements: [
    createActivityNameControl('Activity Name *', 'Enter game name', formData)
  ]
});

// Schema for Game Activity Availability (includes time fields)
export const gameAvailabilityJsonSchema = {
  type: 'object',
  properties: {
    startDate: {type: 'string'},
    endDate: {type: 'string'},
    dailyDisplayStartTime: {type: 'string'},
    dailyDisplayEndTime: {type: 'string'}
  },
  required: []
};

// UI Schema for Game Activity Availability (dates only)
export const gameAvailabilityUiSchema = (startDate, endDate, formData) => {
  const disable = shouldDisableGameStartDate(formData);
  return {
    type: 'VerticalLayout',
    className: 'partner-filters',
    elements: [
      {
        type: 'HorizontalLayout',
        className: 'w-full activity-section_availability-block',
        elements: [
          createDateControl(
            '#/properties/startDate',
            'Active Start Date *',
            'Choose date',
            disable,
            disable
              ? ''
              : 'Please select end date before selecting start date.',
            endDate,
            [
              {type: 'required', msg: ` is required`},
              {
                type: DATE_VALIDATION_TYPE.START_DATE,
                msg: ` can't be in the past.`,
                endDate: endDate
              },
              {
                type: DATE_VALIDATION_TYPE.START_DATE_TODAY,
                msg: `  Target can only start from a date in future`
              }
            ]
          ),
          createDateControl(
            '#/properties/endDate',
            'Active End Date *',
            'Choose date',
            !startDate,
            'Please select start date before selecting end date.',
            startDate,
            [
              {type: 'required', msg: ` is required`},
              {
                type: DATE_VALIDATION_TYPE.END_DATE,
                msg: 'End Date cannot be earlier than Start Date.',
                startDate: startDate,
                excludeLabel: true
              }
            ]
          )
        ]
      }
    ]
  };
};

// Schema for Game Activity Time fields
export const gameTimeFieldsJsonSchema = {
  type: 'object',
  properties: {
    dailyDisplayStartTime: {type: 'string'},
    dailyDisplayEndTime: {type: 'string'}
  },
  required: []
};

// UI Schema for Game Activity Time fields
export const gameTimeFieldsUiSchema = () => ({
  type: 'HorizontalLayout',
  className: 'flex flex-wrap items-end gap-4',
  elements: [
    createTextfieldControl(
      '#/properties/dailyDisplayStartTime',
      'Daily Display Start Time',
      'hh:mm',
      'box-size-width text-sm',
      false,
      []
    ),
    createTextfieldControl(
      '#/properties/dailyDisplayEndTime',
      'Daily Display End Time',
      'hh:mm',
      'box-size-width text-sm',
      false,
      []
    )
  ]
});

// Schema for Currency to Points Conversion Activity
export const currencyToPointsJsonSchema = {
  type: 'object',
  properties: {
    localCurrency: {
      type: 'number',
      helperText: '',
      errorMessage: 'This field is required'
    },
    pointsEarned: {
      type: 'number',
      helperText: '',
      errorMessage: 'This field is required'
    }
  },
  required: []
};

// UI Schema for Currency to Points Conversion Activity
export const currencyToPointsUiSchema = (localCurrencyCode) => {
  const currencyLabel = localCurrencyCode
    ? `Local Currency (${localCurrencyCode}) *`
    : 'Local Currency *';
  return {
    type: 'HorizontalLayout',
    className: 'w-full',
    elements: [
      {
        type: 'HorizontalLayout',
        className: 'w-auto flex gap-4',
        elements: [
          createTextfieldControl(
            '#/properties/localCurrency',
            currencyLabel,
            'Enter amount',
            'box-size-width text-sm',
            true,
            [
              {type: 'required', msg: ` is required`},
              {type: 'decimal', msg: 'must be a number'},
              {
                type: 'pattern',
                regex: '^[0-9]+(\\.[0-9]{1,2})?$',
                msg: 'must be a positive number'
              }
            ]
          ),
          createTextfieldControl(
            '#/properties/pointsEarned',
            'Points Earned *',
            'Enter points',
            'box-size-width hide-cross-icon text-sm',
            true,
            [
              {type: 'required', msg: ` is required`},
              {type: 'decimal', msg: 'must be a number'},
              {
                type: 'pattern',
                regex: '^[0-9]+(\\.[0-9]{1,2})?$',
                msg: 'must be a positive number'
              }
            ]
          )
        ]
      }
    ]
  };
};

/** Easy Order activity — points fields (counter rendered standalone in setup UI) */
export const easyOrderActivityJsonSchema = {
  type: 'object',
  properties: {
    pointsForSuccessfulUpload: {
      type: 'string',
      restrictDecimal: false
    },
    pointsPerLineItem: {
      type: 'string',
      restrictDecimal: false
    }
  },
  required: []
};

export const easyOrderActivityUiSchema = () => ({
  type: 'HorizontalLayout',
  className:
    'easy-order-activity-fields flex flex-wrap items-end gap-4 lg:flex-nowrap',
  elements: [
    createTextfieldControl(
      '#/properties/pointsForSuccessfulUpload',
      'Points for Successful Upload *',
      'Points for Successful Upload',
      'box-size-width text-sm',
      true,
      [
        {type: 'required', msg: ` is required`},
        {type: 'number', msg: ` must be a number`},
        {type: 'custom', validate: validateNonNegativeNumber}
      ]
    ),
    createTextfieldControl(
      '#/properties/pointsPerLineItem',
      'Points per Line Item *',
      'Points per Line Item',
      'box-size-width text-sm',
      true,
      [
        {type: 'required', msg: ` is required`},
        {type: 'number', msg: ` must be a number`},
        {type: 'custom', validate: validateNonNegativeNumber}
      ]
    )
  ]
});
